import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Lock, Sparkles, Users } from "lucide-react";

export default function ModulePage() {
  const { moduleId } = useParams();
  const { couple, loading: authLoading, role } = useAuth();
  const isSolo = couple?.is_solo;
  const modulePrice = isSolo ? "3,99€" : "5,99€";
  const [module, setModule] = useState(null);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [compare, setCompare] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [editing, setEditing] = useState(false);
  const [searchParams] = useSearchParams();
  const nav = useNavigate();

  useEffect(() => {
    if (searchParams.get("edit") === "1") {
      setEditing(true);
      setCurrent(0);
    }
  }, [searchParams]);

  useEffect(() => {
    if (authLoading) return;
    if (!couple) {
      nav("/onboarding");
      return;
    }
    loadModule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, couple, authLoading]);

  const loadModule = async () => {
    setLoading(true);
    try {
      const [{ data: md }, { data: st }] = await Promise.all([
        api.get(`/modules/${moduleId}`),
        api.get(`/modules/${moduleId}/status`),
      ]);
      setModule(md.module);
      setAnswers(md.my_answers || {});
      setStatus(st);
      if (st?.both_done && !isSolo) {
        try {
          const { data: cmp } = await api.get(`/modules/${moduleId}/compare`);
          setCompare(cmp);
        } catch (e) {
          /* ignore */
        }
      }
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!editing && status?.analysis_ready) {
      nav(`/module/${moduleId}/analysis`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, editing, moduleId]);

  const setAns = (qid, val) => setAnswers((p) => ({ ...p, [qid]: val }));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/modules/${moduleId}/answers`, { answers });
      const { data: st } = await api.get(`/modules/${moduleId}/status`);
      setStatus(st);
      if (st?.both_done && !isSolo) {
        try {
          const { data: cmp } = await api.get(`/modules/${moduleId}/compare`);
          setCompare(cmp);
        } catch (e) {
          /* ignore */
        }
      }
      if (editing) {
        setEditing(false);
        setCurrent(0);
        if (st?.analysis_ready) {
          toast.success("Réponses mises à jour. Pensez à régénérer votre bilan pour l'affiner.");
          nav(`/module/${moduleId}/analysis`);
          return;
        }
        toast.success("Vos réponses ont été mises à jour.");
      } else {
        toast.success("Tes réponses sont enregistrées.");
      }
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/modules/${moduleId}/analysis`);
      toast.success("Analyse générée !");
      nav(`/module/${moduleId}/analysis`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de génération");
    } finally {
      setGenerating(false);
    }
  };

  const unlock = async () => {
    setUnlocking(true);
    try {
      const { data } = await api.post(`/payments/checkout`, {
        item_type: "module",
        module_id: moduleId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lancer le paiement.");
      setUnlocking(false);
    }
  };

  if (loading || !module)
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center text-[#8A8480]">
        Chargement…
      </div>
    );

  const myDone = Object.keys(answers).length === module.questions.length &&
                 module.questions.every((q) => answers[q.id] !== undefined && answers[q.id] !== "");

  // "En attente" seulement si J'AI DÉJÀ SOUMIS (selon le statut réel) et pas le partenaire.
  const iSubmitted = role === "a" ? status?.partner_a_done : status?.partner_b_done;

  if (!editing && status && iSubmitted && !status.both_done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center animate-fade-in-up">
        <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] mb-4 font-semibold">
          En attente
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight mb-6">
          Tes réponses sont <span className="italic">prêtes</span>.
        </h1>
        <p className="text-[#5C5754] text-lg leading-relaxed mb-12">
          Nous patientons sereinement que ton/ta partenaire complète aussi le module.
          L'analyse sera disponible dès que vous aurez tous deux répondu.
        </p>
        <div className="rounded-3xl overflow-hidden mb-10">
          <img
            src="https://images.unsplash.com/photo-1513267000941-598abe7be16f?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Mains entrelacées"
            className="w-full h-72 object-cover"
          />
        </div>
        <Link
          to="/dashboard"
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-2 text-[#C86B5E] hover:text-[#B35A4E]"
        >
          <ArrowLeft className="w-4 h-4" /> Retour au parcours
        </Link>
        <div className="mt-6">
          <button
            data-testid="edit-answers-button"
            onClick={() => { setEditing(true); setCurrent(0); }}
            className="text-sm text-[#8A8480] hover:text-[#C86B5E] underline"
          >
            Ajuster mes réponses
          </button>
        </div>
      </div>
    );
  }

  if (!editing && status?.both_done && !status?.analysis_ready) {
    const unlocked = status?.unlocked;
    return (
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16 animate-fade-in-up">
        <Link
          to="/dashboard"
          data-testid="module-back-link"
          className="inline-flex items-center gap-2 text-sm text-[#8A8480] hover:text-[#C86B5E] mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> Parcours
        </Link>
        <p className="text-xs tracking-[0.3em] uppercase text-[#C86B5E] mb-3 font-semibold">
          {module.title} · {isSolo ? "Vos réponses sont prêtes" : "Vous avez tous deux répondu"}
        </p>
        <div className="mb-6">
          <button
            data-testid="edit-answers-button"
            onClick={() => { setEditing(true); setCurrent(0); }}
            className="text-sm text-[#8A8480] hover:text-[#C86B5E] underline"
          >
            Ajuster mes réponses
          </button>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight mb-4">
          {isSolo ? (
            <>Votre <span className="italic">bilan personnel</span>.</>
          ) : (
            <>Vos réponses, <span className="italic">côte à côte</span>.</>
          )}
        </h1>
        <p className="text-[#5C5754] text-lg leading-relaxed mb-10">
          {isSolo
            ? "Débloquez votre bilan d'auto-analyse : score de clarté, forces personnelles, points de vigilance, conseils et plan de croissance."
            : "Comparez librement vos réponses ci-dessous. Pour aller plus loin, débloquez l'analyse approfondie (psychologie, conseil matrimonial, sagesse biblique, plan d'action)."}
        </p>

        {/* Side-by-side comparison */}
        {compare && (
          <div className="space-y-4 mb-12" data-testid="compare-view">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#C86B5E]">
                <Users className="w-4 h-4" /> {compare.partner_a.name}
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold text-[#7A8B76]">
                <Users className="w-4 h-4" /> {compare.partner_b.name}
              </div>
            </div>
            {compare.questions.map((q) => (
              <div key={q.id} className="bg-white border border-[#E6E2DD] rounded-2xl p-6">
                <p className="font-heading text-lg text-[#2C2A29] mb-4 leading-snug">{q.text}</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-[#FAF9F6] border border-[#F3EFEB] rounded-xl p-4">
                    <p className="text-xs uppercase tracking-wider text-[#C86B5E] font-semibold mb-2">
                      {compare.partner_a.name}
                    </p>
                    <p className="text-[#2C2A29] leading-relaxed whitespace-pre-wrap">
                      {q.partner_a_answer ?? "—"}
                    </p>
                  </div>
                  <div className="bg-[#FAF9F6] border border-[#F3EFEB] rounded-xl p-4">
                    <p className="text-xs uppercase tracking-wider text-[#7A8B76] font-semibold mb-2">
                      {compare.partner_b.name}
                    </p>
                    <p className="text-[#2C2A29] leading-relaxed whitespace-pre-wrap">
                      {q.partner_b_answer ?? "—"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Teased preview (locked only) */}
        {!unlocked && (
          <div
            data-testid="analysis-teaser"
            className="relative bg-white border border-[#E6E2DD] rounded-3xl p-8 md:p-10 mb-6 overflow-hidden"
          >
            <div className="blur-[6px] select-none pointer-events-none" aria-hidden="true">
              <p className="text-xs tracking-[0.3em] uppercase text-[#8A8480] font-semibold mb-6 text-center">
                Score d'alignement
              </p>
              <div className="flex justify-center mb-8">
                <div className="relative w-32 h-32 rounded-full border-[10px] border-[#C86B5E]/40 flex items-center justify-center">
                  <span className="font-heading text-5xl text-[#C86B5E]">87</span>
                </div>
              </div>
              <div className="space-y-3 max-w-xl mx-auto">
                <div className="h-3 rounded-full bg-[#E6E2DD] w-full" />
                <div className="h-3 rounded-full bg-[#E6E2DD] w-11/12" />
                <div className="h-3 rounded-full bg-[#E6E2DD] w-4/5" />
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-8">
                <div className="bg-[#F3EFEB] rounded-2xl p-5 space-y-2">
                  <div className="h-3 rounded-full bg-[#E6E2DD] w-2/3" />
                  <div className="h-3 rounded-full bg-[#E6E2DD] w-full" />
                  <div className="h-3 rounded-full bg-[#E6E2DD] w-5/6" />
                </div>
                <div className="bg-[#F3EFEB] rounded-2xl p-5 space-y-2">
                  <div className="h-3 rounded-full bg-[#E6E2DD] w-1/2" />
                  <div className="h-3 rounded-full bg-[#E6E2DD] w-full" />
                  <div className="h-3 rounded-full bg-[#E6E2DD] w-3/4" />
                </div>
              </div>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-t from-white via-white/85 to-white/50">
              <div className="w-12 h-12 rounded-full bg-[#C86B5E] flex items-center justify-center mb-4 shadow-lg">
                <Lock className="w-5 h-5 text-white" strokeWidth={1.75} />
              </div>
              <p className="font-heading text-2xl text-[#2C2A29] text-center px-6">
                Votre analyse vous attend
              </p>
              <p className="text-sm text-[#5C5754] mt-2 text-center px-8 max-w-sm">
                {isSolo
                  ? "Score de clarté, forces, points de vigilance, conseils et plan de croissance — révélés dès le déblocage."
                  : "Score de compatibilité, forces, points à explorer, conseils et plan d'action — révélés dès le déblocage."}
              </p>
            </div>
          </div>
        )}

        {/* Analysis gating */}
        <div className="bg-[#F3EFEB] border border-[#E6DCD3] rounded-3xl p-8 md:p-10 text-center">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mx-auto mb-5">
            {unlocked ? (
              <Sparkles className="w-5 h-5 text-[#C86B5E]" strokeWidth={1.5} />
            ) : (
              <Lock className="w-5 h-5 text-[#C86B5E]" strokeWidth={1.5} />
            )}
          </div>
          {unlocked ? (
            <>
              <h2 className="font-heading text-3xl tracking-tight mb-3">
                Générons votre <span className="italic">analyse</span>.
              </h2>
              <p className="text-[#5C5754] leading-relaxed mb-8 max-w-xl mx-auto">
                {isSolo
                  ? "Vos réponses sont analysées en profondeur — psychologie, développement personnel, sagesse biblique. Cela prend une minute."
                  : "Vos réponses sont analysées en profondeur — psychologie du couple, conseil matrimonial, sagesse biblique. Cela prend une minute."}
              </p>
              <button
                data-testid="generate-analysis-button"
                onClick={generate}
                disabled={generating}
                className="bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-50 text-white rounded-full px-10 py-4 transition-colors inline-flex items-center gap-3"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Génération en cours…
                  </>
                ) : (
                  <>
                    Générer l'analyse <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <h2 className="font-heading text-3xl tracking-tight mb-3">
                Débloquez l'analyse <span className="italic">approfondie</span>.
              </h2>
              <p className="text-[#5C5754] leading-relaxed mb-8 max-w-xl mx-auto">
                Score d'alignement, lecture psychologique, conseil pastoral et plan d'action
                personnalisé, à partir de vos réponses.
              </p>
              <button
                data-testid="unlock-analysis-button"
                onClick={unlock}
                disabled={unlocking}
                className="bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-50 text-white rounded-full px-10 py-4 transition-colors inline-flex items-center gap-3"
              >
                {unlocking ? "Redirection…" : `Débloquer ${isSolo ? "mon bilan" : "l'analyse"} · ${modulePrice}`}
                <ArrowRight className="w-4 h-4" />
              </button>
              <p className="mt-4 text-sm text-[#8A8480]">
                Ou débloquez les 9 modules avec le{" "}
                <Link to="/dashboard" className="text-[#C86B5E] hover:underline">
                  pack complet à {isSolo ? "19€" : "29€"}
                </Link>
                .
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!editing && status?.analysis_ready) {
    return null;
  }

  const q = module.questions[current];
  const total = module.questions.length;
  const progress = ((current + 1) / total) * 100;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
      <Link
        to="/dashboard"
        data-testid="module-back-link"
        className="inline-flex items-center gap-2 text-sm text-[#8A8480] hover:text-[#C86B5E] mb-10"
      >
        <ArrowLeft className="w-4 h-4" /> Parcours
      </Link>
      {editing && (
        <div className="mb-6 flex items-center justify-between gap-4 bg-[#F3EFEB] border border-[#E6DCD3] rounded-2xl px-5 py-3">
          <p className="text-sm text-[#5C5754]">
            Vous ajustez vos réponses. Elles seront mises à jour à la soumission.
          </p>
          <button
            data-testid="cancel-edit-button"
            onClick={() => { setEditing(false); setCurrent(0); loadModule(); }}
            className="text-sm text-[#C86B5E] hover:underline flex-shrink-0"
          >
            Annuler
          </button>
        </div>
      )}
      <p className="text-xs tracking-[0.3em] uppercase text-[#C86B5E] font-semibold mb-3">
        Module {String(module.order).padStart(2, "0")} · {module.title}
      </p>
      <h1 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-3">
        {module.subtitle}
      </h1>
      <p className="text-[#5C5754] leading-relaxed mb-10">{module.description}</p>

      {/* Progress */}
      <div className="mb-10">
        <div className="flex justify-between text-xs tracking-[0.2em] uppercase text-[#8A8480] mb-2">
          <span>Question {current + 1} / {total}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-1 bg-[#F3EFEB] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#C86B5E] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div key={q.id} className="bg-white border border-[#E6E2DD] rounded-3xl p-8 md:p-10 animate-fade-in-up">
        <h2 className="font-heading text-2xl sm:text-3xl leading-snug mb-8">
          {q.text}
        </h2>

        {q.type === "long_text" && (
          <textarea
            data-testid={`q-${q.id}-input`}
            value={answers[q.id] || ""}
            onChange={(e) => setAns(q.id, e.target.value)}
            rows={5}
            className="w-full bg-[#FAF9F6] border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-5 py-4 text-[#2C2A29] resize-none leading-relaxed"
            placeholder="Prends le temps de répondre avec sincérité…"
          />
        )}

        {q.type === "multiple_choice" && (
          <div className="space-y-3">
            {q.options.map((opt) => (
              <button
                key={opt}
                data-testid={`q-${q.id}-option-${opt}`}
                onClick={() => setAns(q.id, opt)}
                className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                  answers[q.id] === opt
                    ? "border-[#C86B5E] bg-[#C86B5E]/5 text-[#2C2A29]"
                    : "border-[#E6E2DD] bg-[#FAF9F6] hover:border-[#C86B5E]/40"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}

        {q.type === "scale" && (
          <div>
            <div className="flex gap-2 flex-wrap">
              {Array.from({ length: q.max - q.min + 1 }, (_, i) => q.min + i).map((n) => (
                <button
                  key={n}
                  data-testid={`q-${q.id}-scale-${n}`}
                  onClick={() => setAns(q.id, n)}
                  className={`w-12 h-12 rounded-full border transition-all font-medium ${
                    answers[q.id] === n
                      ? "bg-[#C86B5E] text-white border-[#C86B5E]"
                      : "bg-[#FAF9F6] border-[#E6E2DD] hover:border-[#C86B5E] text-[#2C2A29]"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            {q.labels && (
              <div className="flex justify-between mt-4 text-xs text-[#8A8480]">
                <span>{q.labels[String(q.min)]}</span>
                <span>{q.labels[String(q.max)]}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between mt-8">
        <button
          data-testid="prev-question"
          onClick={() => setCurrent((c) => Math.max(0, c - 1))}
          disabled={current === 0}
          className="inline-flex items-center gap-2 text-[#5C5754] hover:text-[#C86B5E] disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" /> Précédente
        </button>

        {current < total - 1 ? (
          <button
            data-testid="next-question"
            onClick={() => setCurrent((c) => Math.min(total - 1, c + 1))}
            disabled={answers[q.id] === undefined || answers[q.id] === ""}
            className="bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-40 text-white rounded-full px-8 py-3 transition-colors inline-flex items-center gap-2"
          >
            Suivante <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            data-testid="submit-module"
            onClick={submit}
            disabled={!myDone || submitting}
            className="bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-40 text-white rounded-full px-8 py-3 transition-colors inline-flex items-center gap-2"
          >
            {submitting ? "Enregistrement…" : editing ? "Mettre à jour mes réponses" : "Soumettre mes réponses"} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
